import { useCallback, useEffect, useRef } from "react";
import type { ChatStatus } from "ai";
import { MonitoringService } from "@/generated";
import type { NoxusUIMessage } from "./noxus-message-types";

const SEND_MARK = "chat:send";
export const FIRST_TOKEN_MEASURE = "chat:first-token";

const isVisible = (part: NoxusUIMessage["parts"][number]) =>
  part.type === "text" ||
  part.type === "reasoning" ||
  part.type.startsWith("tool-") ||
  part.type === "dynamic-tool";

/**
 * How long a user waits for a reply to show anything: from send to the first
 * reasoning, tool call or text of the assistant's message, recorded as a
 * `chat:first-token` performance measure and reported to the backend with the
 * time the first streamed frame arrived.
 */
export function useFirstTokenTiming(
  messages: NoxusUIMessage[],
  status: ChatStatus,
  surface: "genie" | "agent"
) {
  const sentAt = useRef<number | null>(null);
  const firstFrameAt = useRef<number | null>(null);
  const userCount = useRef(0);

  const markSend = useCallback(() => {
    sentAt.current = performance.now();
    firstFrameAt.current = null;
    userCount.current = messages.filter((m) => m.role === "user").length + 1;
    performance.mark(SEND_MARK);
  }, [messages]);

  useEffect(() => {
    const start = sentAt.current;
    if (start === null || (status !== "streaming" && status !== "ready"))
      return;
    if (status === "streaming" && firstFrameAt.current === null)
      firstFrameAt.current = performance.now();
    const users = messages.filter((m) => m.role === "user").length;
    const last = messages[messages.length - 1];
    if (users < userCount.current || last?.role !== "assistant") return;
    if (!last.parts.some(isVisible)) return;
    sentAt.current = null;
    const end = performance.now();
    performance.measure(FIRST_TOKEN_MEASURE, {
      start,
      end,
      detail: { surface },
    });
    const frame = firstFrameAt.current;
    MonitoringService.recordChatTiming({
      body: {
        surface,
        first_frame_ms: frame === null ? null : frame - start,
        first_visible_ms: end - start,
      },
    }).catch(() => undefined);
  }, [messages, status, surface]);

  return markSend;
}
